import React, { useState, useEffect, useCallback } from 'react';
import { Activity, Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';

// Pure fallback data generator — hoisted to module scope so the useCallback
// below doesn't need to close over it. Timestamps are computed fresh on
// each call, which matches the original inline behaviour.
const generateMockActivities = () => [
  { type: 'conversation', icon: '💬', text: 'Had a conversation with Sarah', timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString() },
  { type: 'badge', icon: '🏆', text: 'Earned "Week Warrior" badge', timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString() },
  { type: 'checkin', icon: '✅', text: 'Completed daily check-in', timestamp: new Date(Date.now() - 26 * 60 * 60 * 1000).toISOString() },
  { type: 'group', icon: '👥', text: 'Joined "Conversation Kick-Start" session', timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString() },
  { type: 'hearts', icon: '💖', text: 'Earned 25 hearts', timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString() }
];

const ActivityFeedCard = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  // useCallback Guard: everything `fetchActivities` reads is stable
  // (React setters, the imported `apiClient`, module-scoped
  // `generateMockActivities`). Locking with `[]` gives the mount effect
  // below a stable function reference — no double-fetch.
  const fetchActivities = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.getActivityFeed(10);
      setActivities(data);
    } catch (err) {
      console.error('Failed to fetch activity feed:', err);
      // Use fallback mock data
      setActivities(generateMockActivities());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="card" data-testid="activity-feed-card">
        <div className="card-header">
          <h2 className="card-title">Activity Feed</h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="card" data-testid="activity-feed-card">
      <div className="card-header">
        <h2 className="card-title">Activity Feed</h2>
        <Activity size={18} className="text-gray-400" />
      </div>

      {activities.length === 0 ? (
        <div className="text-center py-6">
          <p className="text-sm text-gray-600">No recent activity</p>
          <p className="text-xs text-gray-600 mt-1">Start a conversation to see activity here</p>
        </div>
      ) : (
        <div className="space-y-3">
          {activities.slice(0, 5).map((activity, index) => (
            <div
              key={index}
              className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <span className="text-xl">{activity.icon}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-700 truncate">{activity.text}</p>
                <p className="text-xs text-gray-600">{formatTime(activity.timestamp)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivityFeedCard;
